import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { toast } from 'sonner';
import api, { organizationsAPI, actionHistoryAPI, preferencesAPI } from '../lib/api';
import { isDemoTab } from '../lib/session';
import { fmtCurrency } from '../lib/financeMath';
import { DemoNotificationsToggle } from '../components/shared/DemoNotificationsToggle';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { formatRelative } from '../lib/datetime';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import {
    LayoutDashboard, Activity, TrendingUp, TrendingDown, AlertTriangle, Star,
    DollarSign, Building2, Users, ChevronRight, Layers, Filter,
    ArrowUpRight, ShieldCheck, Sparkles, Send, DoorOpen, Car, Wrench, Video, Megaphone, ArrowLeft, Thermometer, MessagesSquare, Repeat,
    MoreHorizontal, Archive, ArchiveRestore, Trash2, Clock, Coins,
} from 'lucide-react';
import { CommunicationDrawer, AssignStaffDrawer, DispatchTaskDrawer, ReassignDialog, GroupMessageDrawer } from './commandCenter/actions/ActionDrawers';
import { DepartmentStatusBar } from './commandCenter/DepartmentStatusBar';
import { RequestThread } from './commandCenter/RequestThread';
import { CCTradeInPanel } from './commandCenter/CCTradeInPanel';
import { AutoInquiryVehicle } from './commandCenter/AutoInquiryVehicle';
import BuyerActivityPanel from '../components/showroom/BuyerActivityPanel';
import { LiveNowBadge, useLiveBuyers } from '../components/showroom/LiveNow';
import LiveBuyersButton from '../components/showroom/LiveBuyersButton';
import { OfferDepositCard, isOfferRequest } from '../components/shared/OfferDepositCard';
import { ManagerSalesChat } from './commandCenter/ManagerSalesChat';
import { getVerticalConfig, getFriendlyConfig, CATEGORY_DEPARTMENT_IDS } from './commandCenter/verticalConfig';
import { HelpTooltip, EmptyState } from '../components/UsabilityComponents';
import { Skeleton } from '../components/ui/skeleton';
import { GuidedTour, useGuidedTour } from '../components/shared/GuidedTour';
import { FriendlyTip } from '../components/shared/FriendlyTip';
import { Compass, BookOpen } from 'lucide-react';
import VerticalPanels from './commandCenter/VerticalPanels';
import LoyaltyProgramPanel from './commandCenter/panels/LoyaltyProgramPanel';
import { AlertsDeafBanner } from '../components/AlertsDeafBanner';
import { sortByName } from '../lib/sortByName';
import { rememberCcVertical, announceCcScopeChange, CC_ORG_STORAGE_KEY } from '../lib/vertical';
import ActionTimeline from '../components/shared/ActionTimeline';
import RecentActivity from './commandCenter/RecentActivity';
import { AutomotiveSalesTempDialog } from './commandCenter/panels/AutomotiveSalesTempPanel';
import ReviewLinksRow from './commandCenter/ReviewLinksRow';

const CATEGORY_DEPTS = new Set(CATEGORY_DEPARTMENT_IDS);
// Automotive request kinds: the shared Open Requests supports Resolve (confirm /
// close) + Cancel (decline / close) for these; Acknowledge and Reassign are
// UNAVAILABLE (no existing automotive acknowledgment / salesperson-assignment
// model) and are hidden rather than shown as no-ops.
const AUTOMOTIVE_KINDS = new Set(['vehicle_inquiry', 'test_drive', 'trade_in']);
// Deterministic status → indicator colour (matches the department dot semantics):
// red = open / needs attention · amber = acknowledged / being handled · green = resolved.
const REQ_DOT = (status) => {
    if (['open', 'retrieval_requested'].includes(status)) return 'bg-red-500';
    if (['acknowledged', 'retrieving', 'ready'].includes(status)) return 'bg-amber-400';
    if (['resolved', 'delivered'].includes(status)) return 'bg-emerald-500';
    return 'bg-slate-500';
};
// Map a department tile → the existing request rows it owns (reuses kind/category).
const deptMatchesRequest = (r, deptId) => {
    if (!deptId) return true;
    // The backend now states which department a request is in (routing layer).
    // Trust it when present; fall back to the original derivation otherwise.
    if (r.department) return r.department === deptId;
    if (CATEGORY_DEPTS.has(deptId)) return r.category === deptId;
    if (deptId === 'security') return r.kind === 'security' || r.category === 'security';
    if (deptId === 'concierge') return r.kind === 'concierge' && !CATEGORY_DEPTS.has(r.category) && r.category !== 'security';
    if (deptId === 'valet') return r.kind === 'valet';
    if (deptId === 'housekeeping') return r.category === 'housekeeping';
    return r.category === deptId;
};

const VERTICAL_TITLE_KEY = {
    restaurant: 'commandCenter.title.restaurant',
    hotel: 'commandCenter.title.hotel',
    condo: 'commandCenter.title.condo',
    property_management: 'commandCenter.title.property_management',
};

// Overview cards: columns always divide the card count, so a row never ends
// with a single orphan card. Shared by every vertical.
const OVERVIEW_GRID = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-5',
    6: 'grid-cols-2 lg:grid-cols-3',
};

const SEVERITY_STYLE = {
    high: { dot: 'bg-red-400', text: 'text-red-300', ring: 'border-red-500/30 bg-red-500/5' },
    moderate: { dot: 'bg-amber-400', text: 'text-amber-300', ring: 'border-amber-500/30 bg-amber-500/5' },
    low: { dot: 'bg-slate-400', text: 'text-slate-600', ring: 'border-slate-600/40 bg-slate-700/10' },
};

const fmtMoney = (v) => fmtCurrency(v);

// Governance pulse stores i18n keys in title/description. Render a calm,
// human label without depending on the operational-base key bundle:
// prefer a real sentence, otherwise humanize the last key segment.
const humanizeActivity = (a) => {
    const pick = [a?.title, a?.description].find((s) => s && !/^[a-z0-9_]+(\.[a-z0-9_]+)+$/i.test(String(s)));
    if (pick) return pick;
    const key = a?.title || a?.description || '';
    const seg = String(key).split('.').filter((p) => p && p !== 'title' && p !== 'description').pop() || 'Activity';
    return seg.replace(/[_-]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

function DeltaPill({ pct }) {
    if (pct === null || pct === undefined) return null;
    const up = pct >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span
            data-testid="cc-delta-pill"
            className={`inline-flex items-center gap-1 text-xs font-semibold ${up ? 'text-emerald-400' : 'text-red-400'}`}
        >
            <Icon className="w-3.5 h-3.5" />{up ? '+' : ''}{pct}%
        </span>
    );
}

function StatCard({ testid, icon: Icon, label, value, sub, accent }) {
    return (
        <Card data-testid={testid} className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none backdrop-blur">
            <CardContent className="p-5">
                <div className="flex items-center gap-2 text-slate-600 mb-2">
                    <Icon className={`w-4 h-4 ${accent || 'text-emerald-400'}`} />
                    <span className="text-xs uppercase tracking-wide">{label}</span>
                </div>
                <div className="text-3xl font-bold text-slate-900 dark:text-white leading-none">{value}</div>
                {sub && <div className="mt-2 text-xs text-slate-600">{sub}</div>}
            </CardContent>
        </Card>
    );
}

// Friendly headline: large light text with ONE bold word and a trailing period.
// The bold word lives in the i18n string (<b>…</b>) so EN/FR/ES each choose
// their own word — nothing hardcoded here.
function FriendlyHeadline({ i18nKey, className, testid }) {
    return (
        <span
            data-testid={testid}
            className={`block font-light tracking-tight text-slate-900 dark:text-white break-words ${className || 'text-2xl sm:text-3xl'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}
        >
            <Trans i18nKey={i18nKey} components={{ b: <span className="font-extrabold" /> }} />
        </span>
    );
}

const fmtHeadline = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());

const ROOM_REASON_PRESETS = ['deepClean', 'maintenance', 'guestComplaint', 'inspection'];

// The ONE place the Command Center remembers which organization it is scoped
// to, so a refresh (or a return from a secondary page without the query
// string) keeps the same org instead of silently falling back to "all".
// The key itself is owned by lib/vertical.js, which also reads it to decide
// where Card Fleet Ops lives for a super admin.
const readStoredCcOrg = () => {
    try { return localStorage.getItem(CC_ORG_STORAGE_KEY) || ''; } catch { return ''; }
};


export default function CommandCenter() {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const demoReturn = typeof window !== 'undefined' ? sessionStorage.getItem('yulontap_demo_return') : null;
    const { user, isSuperAdmin, patchUser } = useAuth();

    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    // Scope state
    const [orgs, setOrgs] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState(
        () => searchParams.get('org') || readStoredCcOrg() || 'all'
    );
    // Keep the selected client (org) in the URL so returning from a secondary
    // page (Cameras, etc.) restores the same client's Command Center context,
    // and in localStorage so a plain refresh restores it too.
    const handleOrgChange = (v) => {
        if (v === selectedOrg) return;
        try { localStorage.setItem(CC_ORG_STORAGE_KEY, v && v !== 'all' ? v : ''); } catch { /* private mode */ }
        setSelectedOrg(v);
        setSearchParams(v && v !== 'all' ? { org: v } : {}, { replace: true });
        // Both navigation shells read the selected org to decide whether Card
        // Fleet Ops is embedded here — tell them immediately.
        announceCcScopeChange();
    };
    const [selectedLocIds, setSelectedLocIds] = useState([]); // [] = ALL
    const [scopeOpen, setScopeOpen] = useState(false);
    // The calm attention pill starts collapsed — nobody is confronted with a list.
    const [attentionOpen, setAttentionOpen] = useState(false);
    const [commOpen, setCommOpen] = useState(false);
    const [announceOpen, setAnnounceOpen] = useState(false);
    const [dispatchOpen, setDispatchOpen] = useState(false);
    const [salesTempOpen, setSalesTempOpen] = useState(false);
    const [dispatchPreset, setDispatchPreset] = useState('');
    const [reassign, setReassign] = useState(null);
    const [assignOpen, setAssignOpen] = useState(false);
    const [roomActOpen, setRoomActOpen] = useState(false);
    const [raLoc, setRaLoc] = useState('');
    const [raRoom, setRaRoom] = useState('');
    const [raAction, setRaAction] = useState('ready');
    const [raReason, setRaReason] = useState('');

    const superAdmin = typeof isSuperAdmin === 'function' ? isSuperAdmin() : user?.role === 'super_admin';

    useEffect(() => {
        if (!superAdmin) return;
        organizationsAPI.list().then((r) => setOrgs(sortByName(r.data || []))).catch(() => setOrgs([]));
    }, [superAdmin]);

    const fetchSeqRef = useRef(0);
    const fetchAbortRef = useRef(null);
    const fetchOverview = useCallback(async (orgVal, locIds) => {
        // Every request is stamped. Only the NEWEST one may write to the page,
        // and the one it replaces is aborted — a slow response for the previous
        // organization can never repaint the organization now on screen.
        const seq = ++fetchSeqRef.current;
        if (fetchAbortRef.current) fetchAbortRef.current.abort();
        const controller = new AbortController();
        fetchAbortRef.current = controller;
        setLoading(true);
        setError(false);
        try {
            const params = {};
            if (superAdmin && orgVal && orgVal !== 'all') params.organization_id = orgVal;
            if (locIds && locIds.length > 0) params.location_ids = locIds.join(',');
            // Archive is a server-side view of the SAME queue (one predicate).
            params.requests_view = reqViewRef.current === 'archived' ? 'archived' : 'open';
            const res = await api.get('/command-center/overview', { params, signal: controller.signal });
            if (seq !== fetchSeqRef.current) return;
            setData(res.data);
        } catch (e) {
            if (seq !== fetchSeqRef.current) return;
            if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError' || e?.name === 'AbortError') return;
            setError(true);
        } finally {
            if (seq === fetchSeqRef.current) setLoading(false);
        }
    }, [superAdmin]);

    // Initial + on org change → reset location subset (scope changed context)
    // and DROP the previous organization's payload, so the page shows the
    // skeleton until the new organization's own data lands. Never the old one.
    useEffect(() => {
        setSelectedLocIds([]);
        setData(null);
        setError(false);
        if (selectedOrg && selectedOrg !== 'all' && searchParams.get('org') !== selectedOrg) {
            setSearchParams({ org: selectedOrg }, { replace: true });
        }
        fetchOverview(selectedOrg, []);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedOrg, fetchOverview]);

    // Live overview: silent 5s refresh honouring the CURRENT org + property
    // scope. Reuses fetchOverview (same endpoint + backend scope enforcement);
    // single interval, replaced when scope changes, cleaned up on unmount.
    // No spinner flicker — the full-page loader is gated by (loading && !data).
    useEffect(() => {
        const iv = setInterval(() => {
            fetchOverview(selectedOrg, selectedLocIds);
        }, 5000);
        // A presenter switching tabs / role views must never look at stale tiles:
        // browsers throttle timers in hidden tabs, so refetch the moment this
        // view becomes visible again. Same endpoint, same scope enforcement.
        const wake = () => {
            if (document.visibilityState === 'visible') fetchOverview(selectedOrg, selectedLocIds);
        };
        document.addEventListener('visibilitychange', wake);
        window.addEventListener('focus', wake);
        return () => {
            clearInterval(iv);
            document.removeEventListener('visibilitychange', wake);
            window.removeEventListener('focus', wake);
        };
    }, [selectedOrg, selectedLocIds, fetchOverview]);

    const available = sortByName(data?.available_locations || []);
    const scope = data?.scope || {};
    const vertical = data?.vertical || 'restaurant';
    // The scope decides where Card Fleet Ops lives for a super admin: record
    // the vertical this scope resolved to, AGAINST THE ORGANIZATION it was
    // resolved for, so both navigation shells agree and switching clients
    // moves the menu entry with them.
    useEffect(() => {
        if (data?.vertical) rememberCcVertical(user?.id, data.vertical, selectedOrg);
    }, [data?.vertical, user?.id, selectedOrg]);

    // Review / social pages the locations in scope already have links for.
    // Read-only; the row renders nothing when nothing is configured.
    const [reviewLinks, setReviewLinks] = useState([]);
    useEffect(() => {
        let alive = true;
        const params = {};
        if (superAdmin && selectedOrg && selectedOrg !== 'all') params.organization_id = selectedOrg;
        if (selectedLocIds.length > 0) params.location_ids = selectedLocIds.join(',');
        api.get('/command-center/review-links', { params })
            .then((r) => { if (alive) setReviewLinks(r.data?.platforms || []); })
            .catch(() => { if (alive) setReviewLinks([]); });
        return () => { alive = false; };
    }, [superAdmin, selectedOrg, selectedLocIds]);
    // Friendly-UX pass: per-vertical copy/config. No entry => no tour, no tip.
    const friendly = getFriendlyConfig(vertical);
    // Auto-open rules, one place. The account preference wins; super admins and
    // demo organizations never get it unprompted (it lands mid-pitch); a
    // vertical can also opt out via `autoStartTour: false`.
    const tourAutoStart = user?.show_guided_tour !== false
        && !isSuperAdmin()
        && !user?.organization_is_demo
        && !user?.is_demo_manager
        && friendly?.autoStartTour !== false;
    const tour = useGuidedTour(friendly?.tourId, !!friendly, tourAutoStart);
    // Finishing, skipping or "don't show again" saves OFF on the account, so it
    // stays off across logins, browsers and devices.
    const disableTourForAccount = useCallback(() => {
        if (user?.show_guided_tour === false) return;
        preferencesAPI.update({ show_guided_tour: false })
            .then(() => patchUser({ show_guided_tour: false }))
            .catch(() => { /* local close already happened */ });
    }, [user?.show_guided_tour, patchUser]);
    const titleVertical = data?.org_vertical === 'property_management' ? 'property_management' : vertical;
    void titleVertical; // vertical retained for downstream scope logic; title is now fixed branding

    // Shared-shell wording for this vertical ("properties" vs "locations").
    const wording = getVerticalConfig(vertical).wording || {};

    const scopeLabel = useMemo(() => {
        if (selectedLocIds.length === 0) return t(wording.scopeAll || 'commandCenter.scope.all');
        if (selectedLocIds.length === 1) {
            const l = available.find((x) => x.id === selectedLocIds[0]);
            return l ? l.name : t(wording.scopeOne || 'commandCenter.scope.one');
        }
        return t('commandCenter.scope.selectedN', { count: selectedLocIds.length });
    }, [selectedLocIds, available, t, wording.scopeAll, wording.scopeOne]);

    const applyScope = (ids) => {
        setSelectedLocIds(ids);
        setScopeOpen(false);
        fetchOverview(selectedOrg, ids);
    };

    // "Take a look" — never leave someone on an unfiltered list.
    // Restaurant: open Review Shield already filtered to that restaurant and
    // Open, as a real URL so it can be reopened or bookmarked, with a Back
    // that returns to this exact Command Center scope.
    // Ops verticals (hotel / property / automotive) have no Review Shield
    // list, so we stay here, scope to that location and jump to its open list.
    const takeALook = (n) => {
        if (!n?.location_id) return;
        if (isOps) {
            setReqTab('open');
            setReqView('open');
            reqViewRef.current = 'open';
            setDeptFilter(null);
            setReqLimit(10);
            applyScope([n.location_id]);
            setTimeout(() => document.querySelector('[data-testid="cc-band-requests"]')
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 350);
            return;
        }
        const params = new URLSearchParams({ status: 'open', location_id: n.location_id, from: 'cc' });
        const orgId = n.organization_id
            || (available.find((l) => l.id === n.location_id) || {}).organization_id;
        if (orgId) params.set('organization_id', orgId);
        params.set('return_to', `${window.location.pathname}${window.location.search}`);
        navigate(`/review-shield-results?${params.toString()}`);
    };

    const toggleLoc = (id) => {
        setSelectedLocIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    };

    // Phase 3B: acknowledge/resolve a request inline from Command Center.
    const [actingId, setActingId] = useState(null);
    const [deptFilter, setDeptFilter] = useState(null);
    const [focusDept, setFocusDept] = useState(null);
    const [detailReq, setDetailReq] = useState(null);
    const [buyerActivityFor, setBuyerActivityFor] = useState(null);
    // Requests band tabs: Open Requests (unchanged) | Recent Activity (history).
    const [reqTab, setReqTab] = useState('open');
    const [recentUnseen, setRecentUnseen] = useState(0);
    const [salesChatOpen, setSalesChatOpen] = useState(false);
    // Live/Needs-Attention rows (in AutomotiveFunnelPanel, a registry-driven sibling)
    // ask us to open the ENRICHED inquiry dialog via a window event — decoupled so
    // both surfaces share one modal instead of the funnel's smaller local one.
    const dataRef = useRef(null);
    // Which buyers are on the page right now — the same one-call board lookup
    // the Sales Desk uses, so a manager sees it without hunting for it too.
    // Declared up here with the other hooks: below the early returns it would
    // be a conditional hook call.
    const liveBoardOrgId = (selectedOrg && selectedOrg !== 'all') ? selectedOrg
        : ((scope.selected_org_ids && scope.selected_org_ids[0]) || data?.organization_id || user?.organization_id);
    const liveBuyerIds = useLiveBuyers(liveBoardOrgId, vertical === 'automotive');

    // `?live=1` — the split-screen right pane in Manager mode. Open the Buyer
    // Activity panel on whoever is on the page right now, already watching.
    const ccAutoLive = useRef(searchParams.get('live') === '1');
    const ccAutoOpened = useRef(false);
    useEffect(() => {
        if (!ccAutoLive.current || ccAutoOpened.current || buyerActivityFor) return;
        const first = [...liveBuyerIds][0];
        if (!first) return;
        ccAutoOpened.current = true;
        setBuyerActivityFor(first);
    }, [liveBuyerIds, buyerActivityFor]);

    // `?panel=vip-rewards` — the navigation entry for the loyalty program. The
    // org effect below rewrites the query string, so the intent is kept in state.
    const [vipFocus, setVipFocus] = useState(searchParams.get('panel') === 'vip-rewards');
    useEffect(() => {
        if (searchParams.get('panel') === 'vip-rewards') setVipFocus(true);
    }, [searchParams]);
    const vipScrolled = useRef(false);
    useEffect(() => {
        if (!vipFocus || vipScrolled.current || loading) return;
        const el = document.querySelector('[data-testid="cc-band-loyalty"]')
            || document.querySelector('[data-testid="cc-loyalty-pick-clinic"]');
        if (!el) return;
        vipScrolled.current = true;
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    }, [vipFocus, loading, data]);

    // `?focus=fleet` — an old Card Fleet Ops link/bookmark. Land ON the fleet
    // block, not at the top of the page. One-shot, once the data has painted.
    const fleetFocused = useRef(false);
    useEffect(() => {
        if (fleetFocused.current || searchParams.get('focus') !== 'fleet' || loading) return;
        const el = document.querySelector('[data-testid="cc-band-card-fleet"]');
        if (!el) return;
        fleetFocused.current = true;
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 250);
    }, [searchParams, loading, data]);


    useEffect(() => { dataRef.current = data; }, [data]);
    useEffect(() => {
        const onOpen = (e) => {
            const id = e?.detail?.id;
            const reqs = dataRef.current?.requests || [];
            const match = reqs.find((r) => r.id === id);
            if (match) { setSalesChatOpen(false); setDetailReq(match); }
        };
        window.addEventListener('cc:open-inquiry', onOpen);
        return () => window.removeEventListener('cc:open-inquiry', onOpen);
    }, []);
    const actRequest = async (kind, id, action) => {
        setActingId(id);
        try {
            await api.post(`/command-center/act/request/${kind}/${id}`, { action });
            toast.success(t(`commandCenter.act.${action === 'acknowledge' ? 'acknowledged' : action === 'cancel' ? 'cancelled' : 'resolved'}`));
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setActingId(null);
        }
    };
    // Manager governance: cancel/remove a request they created or manage.
    const cancelRequest = async (kind, id) => {
        if (!window.confirm(t('commandCenter.act.cancelConfirm'))) return;
        await actRequest(kind, id, 'cancel');
    };

    // ── Request queue (Part B): pills, selection, paging, archive, delete ──
    // Vertical-agnostic: every label comes from i18n, every row from the shared
    // payload. Nothing here knows which industry it is looking at.
    const [reqView, setReqView] = useState('open');            // open | acknowledged | archived
    const [reqSel, setReqSel] = useState([]);                  // [{ kind, id }]
    const [reqLimit, setReqLimit] = useState(10);
    const [deleteReq, setDeleteReq] = useState(null);
    const reqViewRef = useRef('open');
    const canDeleteRequests = ['super_admin', 'client_admin'].includes(user?.role);
    const selKey = (r) => `${r.kind}:${r.id}`;
    const isSelected = (r) => reqSel.some((s) => selKey(s) === selKey(r));
    const toggleSelected = (r) => setReqSel((prev) => (
        prev.some((s) => selKey(s) === selKey(r))
            ? prev.filter((s) => selKey(s) !== selKey(r))
            : [...prev, { kind: r.kind, id: r.id }]
    ));

    const archiveRequests = async (items, archived) => {
        if (!items.length) return;
        setActingId(items[0].id);
        try {
            if (items.length === 1) {
                await api.post(`/command-center/act/request/${items[0].kind}/${items[0].id}/archive`, { archived });
            } else {
                await api.post('/command-center/act/requests/archive-bulk', { items, archived });
            }
            toast.success(t(archived ? 'commandCenter.requests.archivedOk' : 'commandCenter.requests.restoredOk'));
            setReqSel([]);
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setActingId(null);
        }
    };

    const deleteRequestNow = async (r) => {
        setActingId(r.id);
        try {
            await api.delete(`/command-center/act/request/${r.kind}/${r.id}`);
            toast.success(t('commandCenter.requests.deletedOk'));
            setDeleteReq(null);
            setReqSel([]);
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setActingId(null);
        }
    };

    // Phase 3B follow-up: room status + housekeeping ready inline (hotel).
    const [flagRoomId, setFlagRoomId] = useState(null);
    const [roomReason, setRoomReason] = useState('');
    const roomErr = (detail) => {
        const map = { reason_required: 'reasonRequired', invalid_transition: 'invalidTransition', invalid_state: 'invalidTransition', room_not_found: 'failed' };
        return t(`commandCenter.act.${map[detail] || 'failed'}`, t('commandCenter.act.failed'));
    };
    const actRoomStatus = async (r, new_state, reason) => {
        setActingId(r.id);
        try {
            await api.post(`/command-center/act/room/${r.location_id}/${r.room_number}/status`, { new_state, reason });
            toast.success(t('commandCenter.act.roomUpdated'));
            setFlagRoomId(null);
            setRoomReason('');
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(roomErr(e?.response?.data?.detail));
        } finally {
            setActingId(null);
        }
    };

    const actHousekeepingReady = async (r) => {
        setActingId(r.id);
        try {
            await api.post(`/command-center/act/housekeeping/${r.location_id}/${r.room_number}/ready`, {});
            toast.success(t('commandCenter.act.housekeepingReady'));
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setActingId(null);
        }
    };

    // Valet lifecycle inline (hotel): advance / cancel.
    const valetNext = (status) => {
        if (status === 'retrieval_requested' || status === 'retrieving') return { to: 'ready', key: 'valetReady' };
        if (status === 'ready') return { to: 'delivered', key: 'valetDelivered' };
        return null;
    };
    const actValet = async (r, status) => {
        setActingId(r.id);
        try {
            await api.post(`/command-center/act/valet/${r.id}/status`, { status });
            // Deterministic, in-place status change FIRST so the click is never a
            // silent no-op / re-sort; the queue only repositions after the confirmed
            // refetch below.
            setData((prev) => prev
                ? { ...prev, requests: (prev.requests || []).map((x) => x.id === r.id ? { ...x, status } : x) }
                : prev);
            const toastKey = status === 'ready' ? 'valetMarkedReady'
                : status === 'delivered' ? 'valetMarkedDelivered'
                : status === 'cancelled' ? 'valetMarkedCancelled' : 'valetUpdated';
            toast.success(t(`commandCenter.act.${toastKey}`));
            fetchOverview(selectedOrg, selectedLocIds);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('commandCenter.act.failed'));
        } finally {
            setActingId(null);
        }
    };

    if (loading && !data) {
        return (
            <div className="space-y-8 pb-16" data-testid="cc-loading" aria-busy="true">
                <div className="space-y-3">
                    <Skeleton className="h-8 w-56" />
                    <Skeleton className="h-10 w-72" />
                    <Skeleton className="h-4 w-44" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <Skeleton className="h-36 rounded-2xl" />
                    <Skeleton className="h-36 rounded-2xl" />
                    <Skeleton className="h-36 rounded-2xl" />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <Skeleton className="h-64 rounded-2xl lg:col-span-2" />
                    <Skeleton className="h-64 rounded-2xl" />
                </div>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-slate-600" data-testid="cc-error">
                <AlertTriangle className="w-8 h-8" />
                <p>{t('commandCenter.empty.error')}</p>
                <Button variant="outline" data-testid="cc-error-retry" onClick={() => fetchOverview(selectedOrg, selectedLocIds)}>{t('commandCenter.retry')}</Button>
                {/* A salesperson has no Command Center access, so a failure here
                    is a dead end for them. Nobody else sees this. */}
                {user?.role === 'salesperson' && (
                    <Button variant="ghost" data-testid="cc-error-back-sales-desk"
                        onClick={() => navigate('/sales-desk')}>
                        {t('commandCenter.cameras.backToSalesDesk', 'Back to Sales Desk')}
                    </Button>
                )}
            </div>
        );
    }

    const ov = data?.overview || {};
    // Greeting: stored first name only (never derived from the email address).
    const firstName = String(user?.first_name || '').trim()
        || String(user?.name || '').trim().split(/\s+/)[0]
        || '';
    const friendlyStatusCount = friendly?.statusField ? (ov[friendly.statusField] ?? 0) : 0;
    const wc = data?.what_changed || {};
    const showScopePicker = available.length > 1;
    const cfg = getVerticalConfig(vertical);
    const isOps = cfg.showOps;
    // Resolved automotive org id for the Automotive vertical panels (mirrors the
    // prior early-return resolution) with the authenticated user's org as the
    // final fallback (single-org dealer sessions: overview omits organization_id
    // and scope.selected_org_ids).
    const autoOrgId = (selectedOrg && selectedOrg !== 'all') ? selectedOrg
        : ((scope.selected_org_ids && scope.selected_org_ids[0]) || data?.organization_id || user?.organization_id);
    // THE ORGANIZATION THIS SCREEN IS SCOPED TO, with no guessing. A super admin
    // on "All organizations" is scoped to NOTHING ('' = everything), which is
    // why this cannot reuse autoOrgId — that one falls back to the first org in
    // the scope, which would silently narrow a deliberate "all" view.
    const ccScopedOrgId = superAdmin
        ? ((selectedOrg && selectedOrg !== 'all') ? selectedOrg : '')
        : (user?.organization_id || autoOrgId || '');
    // Overview cards reflow so no card is ever left alone on a row: the count is
    // the same conditional set rendered below.
    const overviewCount = cfg.overviewMode === 'automotive' ? (friendly ? 2 : 5)
        : isOps ? (friendly ? 1 : 4) : 4;


    return (
        /* The live-demo bar is fixed to the bottom of the viewport — extra
           bottom padding keeps the last row fully reachable behind it. */
        <div className={`space-y-8 ${isDemoTab() ? 'pb-32' : 'pb-16'}`} data-testid="command-center-page">
            {/* ── Header ── */}
            <div className="flex flex-col gap-5">
                <div className="min-w-0">
                    {demoReturn && (
                        <button onClick={() => { sessionStorage.removeItem('yulontap_demo_return'); window.location.href = demoReturn; }}
                            data-testid="cc-demo-back"
                            className="inline-flex items-center gap-2 mb-3 text-sm text-slate-600 hover:text-white bg-slate-800/70 border border-slate-700 rounded-full px-3 py-1.5 transition-colors">
                            <ArrowLeft className="w-4 h-4" />{t('demo.backToShowroom', 'Back to showroom')}
                        </button>
                    )}
                    <div className="mb-3" data-testid="cc-greeting-block">
                        <p className="text-2xl sm:text-3xl font-light leading-tight text-slate-900 dark:text-white break-words"
                            style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="cc-greeting">
                            {firstName
                                ? <Trans i18nKey="commandCenter.friendly.greetingNamed" values={{ name: firstName }}
                                    components={{ b: <span className="font-extrabold" /> }} />
                                : t('commandCenter.friendly.greetingAnon')}
                        </p>
                        {friendly && (
                            <p className="mt-1.5 text-sm text-slate-600 break-words" data-testid="cc-greeting-status">
                                {friendlyStatusCount > 0
                                    ? t(friendly.statusKey, { count: friendlyStatusCount })
                                    : t('commandCenter.friendly.allCaughtUp')}
                            </p>
                        )}
                    </div>
                    <div className="flex items-baseline flex-wrap gap-x-3 gap-y-1">
                        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 dark:text-white leading-none" data-testid="cc-title">
                            Command Center
                        </h1>
                        <span className="inline-flex items-baseline gap-1.5 leading-none" data-testid="cc-brand" aria-label="by Tapelligence">
                            <span className="text-slate-600 text-[11px] sm:text-xs font-normal">by</span>
                            <span className="inline-flex items-baseline text-base sm:text-lg font-extrabold tracking-tight">
                                <span className="text-[#3B8DF6]">Tap</span>
                                <span className="mx-[1px] inline-block w-[2px] h-4 sm:h-[18px] -skew-x-[18deg] rounded-sm bg-gradient-to-b from-sky-300 via-amber-300 to-amber-500 shadow-[0_0_6px_rgba(251,191,36,0.6)]" />
                                <span className="text-[#F79219]">elligence</span>
                            </span>
                        </span>
                        {friendly && (
                            <Button variant="outline" size="sm" onClick={tour.start} data-testid="cc-help-tour-button"
                                className="gap-1.5 rounded-full bg-white border-slate-200 text-slate-700 dark:bg-slate-900/60 dark:border-slate-700 dark:text-slate-200">
                                <Compass className="w-4 h-4 text-emerald-500" />
                                <span className="max-w-[160px] truncate">{t('commandCenter.friendly.helpButton')}</span>
                            </Button>
                        )}
                    </div>
                    <p className="mt-2 text-slate-600 text-sm">
                        {scope.kind === 'single' && scope.single_location_name
                            ? scope.single_location_name
                            : t(wording.subtitle || 'commandCenter.subtitle', { count: scope.selected_count || 0 })}
                    </p>
                </div>

                {/* ── MANUAL — one tap from here to the step-by-step manual. ── */}
                {superAdmin && (
                    <button type="button" onClick={() => navigate('/manual')}
                            data-testid="cc-manual-tile"
                            className="group w-full sm:w-auto mt-4 inline-flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-5 py-4 text-left hover:border-emerald-500 hover:shadow-lg transition-all duration-200">
                        <span className="grid place-items-center w-10 h-10 rounded-xl bg-emerald-600/10">
                            <BookOpen className="w-5 h-5 text-emerald-500" aria-hidden="true" />
                        </span>
                        <span className="flex-1">
                            <span className="block text-base font-semibold text-slate-900 dark:text-white">Manual</span>
                            <span className="block text-sm text-slate-500">
                                Step-by-step instructions you can send to a client
                            </span>
                        </span>
                        <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-emerald-500 transition-colors" aria-hidden="true" />
                    </button>
                )}
                {isOps && !data?.empty && scope.kind !== 'none' && Array.isArray(data?.departments) && (
                    <DepartmentStatusBar
                        departments={data.departments}
                        vertical={vertical}
                        activeDept={deptFilter}
                        onOpenDepartment={(id) => {
                            // F&I / Insurance owns no rows on the shared request
                            // queue — its work lives in the F&I desk, so the tile
                            // opens that panel instead of filtering nothing.
                            if (id === 'finance_insurance') {
                                setFocusDept({ id, n: Date.now() });
                                setTimeout(() => document.querySelector('[data-testid="cc-tab-fileads"]')
                                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
                                return;
                            }
                            setDeptFilter((cur) => (cur === id ? null : id));
                            setTimeout(() => document.querySelector('[data-testid="cc-band-requests"]')
                                ?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
                        }}
                    />
                )}

                {/* ── Controls: scope selectors (fully visible) then evenly-distributed actions ── */}
                {(isOps || superAdmin || showScopePicker || reviewLinks.length > 0) && (
                <div className="flex flex-col gap-3" data-testid="cc-scope-controller">
                    {(superAdmin || showScopePicker || reviewLinks.length > 0) && (
                    <div className="flex flex-wrap items-center gap-3">
                        {superAdmin && (
                            /* SETUP, not action: the org selector and the demo-notification
                               switch live in one muted strip so they never read as primary
                               actions. Shared shell → same for every vertical. */
                            <div data-testid="cc-setup-strip"
                                className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-2.5 py-2 dark:border-slate-700/60 dark:bg-slate-900/30">
                                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                                    {t('commandCenter.scope.organization')}
                                </span>
                                <Select value={selectedOrg} onValueChange={handleOrgChange}>
                                    <SelectTrigger className="h-9 w-[190px] border-slate-200 bg-white text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200" data-testid="cc-org-select">
                                        <Layers className="w-4 h-4 mr-1 text-slate-500" />
                                        <SelectValue placeholder={t('commandCenter.scope.organization')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">{t('commandCenter.scope.allOrganizations')}</SelectItem>
                                        {orgs.map((o) => (
                                            <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {selectedOrg !== 'all' && (
                                    <DemoNotificationsToggle
                                        organizationId={selectedOrg}
                                        organizationName={(orgs.find((o) => o.id === selectedOrg) || {}).name}
                                    />
                                )}
                            </div>
                        )}
                        {showScopePicker && (
                            <Popover open={scopeOpen} onOpenChange={setScopeOpen}>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 gap-2" data-testid="cc-scope-trigger">
                                        <Filter className="w-4 h-4 text-slate-600" />
                                        <span className="max-w-[180px] truncate">{scopeLabel}</span>
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="end" className="w-80 p-0" data-testid="cc-scope-popover">
                                    <div className="flex items-center justify-between px-3 py-2 border-b border-border">
                                        <span className="text-sm font-medium">{t(wording.selectScope || 'commandCenter.scope.selectProperties')}</span>
                                        <div className="flex gap-2">
                                            <button className="text-xs text-emerald-500 hover:underline" data-testid="cc-scope-all" onClick={() => applyScope([])}>{t(wording.scopeAll || 'commandCenter.scope.all')}</button>
                                        </div>
                                    </div>
                                    <div className="max-h-64 overflow-y-auto py-1">
                                        {available.map((l) => (
                                            <label key={l.id} className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 cursor-pointer text-sm" data-testid={`cc-loc-option-${l.id}`}>
                                                <Checkbox checked={selectedLocIds.includes(l.id)} onCheckedChange={() => toggleLoc(l.id)} />
                                                <span className="truncate">{l.name}</span>
                                            </label>
                                        ))}
                                    </div>
                                    <div className="px-3 py-2 border-t border-border flex justify-end">
                                        <Button size="sm" data-testid="cc-scope-apply" onClick={() => applyScope(selectedLocIds)}>
                                            {t('commandCenter.scope.apply')}
                                        </Button>
                                    </div>
                                </PopoverContent>
                            </Popover>
                        )}
                        {/* Review / social pages these locations already have
                            links for. Shared by every vertical; renders
                            nothing when nothing is configured. Sits right after
                            the setup strip (no `ml-auto`), and the wrapping row
                            drops it under the selectors on a narrow screen. */}
                        <ReviewLinksRow platforms={reviewLinks} />
                        {/* Patron Points set-up — same gate as the existing
                            drill-down line (super admin only) and only for a
                            venue that uses Tap’N Win / Patron Points, so no
                            other organization sees it. Opens the page that
                            already exists; the sidebar's Command Center entry
                            is the way back. */}
                        {superAdmin && ccScopedOrgId
                            && (orgs.find((o) => o.id === ccScopedOrgId) || {}).goal_game_enabled && (
                            <Button variant="outline" data-testid="cc-act-patron-points"
                                onClick={() => navigate(`/patron-points?org=${ccScopedOrgId}`)}
                                className="gap-2 justify-center min-h-[44px] h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Coins className="w-4 h-4 text-amber-500" />
                                {t('commandCenter.patronPointsButton')}
                            </Button>
                        )}
                    </div>
                    )}
                    {/* A vertical with no action buttons gets no empty bordered
                        card. Only affects verticals whose actionMode is 'none'. */}
                    <div data-testid="cc-friendly-actions"
                        className={friendly && cfg.actionMode !== 'none' ? 'rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none' : ''}>
                    {friendly?.actionsCard && (
                        <div className="mb-4">
                            <FriendlyHeadline i18nKey={friendly.actionsCard.titleKey} className="text-xl sm:text-2xl" testid="cc-friendly-actions-title" />
                        </div>
                    )}
                    {cfg.actionMode === 'automotive' && (
                        <div className="flex flex-wrap gap-3" data-testid="cc-automotive-actions">
                            {/* Standalone Buyer Activity for a manager — keyed on the
                                buyer's session, so browsing shoppers who have not
                                messaged anyone are reachable here too. */}
                            <LiveBuyersButton organizationId={liveBoardOrgId} />
                            <Button onClick={() => setSalesTempOpen(true)} data-testid="cc-act-sales-temp"
                                className="gap-2 justify-center h-auto py-2 whitespace-normal text-center leading-tight text-white bg-gradient-to-r from-red-500 via-amber-400 to-emerald-500 hover:opacity-90 border-0">
                                <Thermometer className="w-4 h-4" />{t('commandCenter.automotive.salesTemp', 'Inventory Sales Temp')}
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/command-center/cameras?scope=lot${superAdmin && selectedOrg && selectedOrg !== 'all' ? `&org=${selectedOrg}` : ''}`)} data-testid="cc-act-sales-lot"
                                className="gap-2 justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Video className="w-4 h-4 text-sky-400" />{t('commandCenter.automotive.salesLot', 'Sales Lot')}
                            </Button>
                            <Button variant="outline" onClick={() => { setDispatchPreset(''); setDispatchOpen(true); }} data-testid="cc-act-dispatch"
                                className="gap-2 justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Wrench className="w-4 h-4 text-slate-600" />{t('commandCenter.act.dispatch.title', 'Dispatch Task')}
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/used-vehicles${autoOrgId ? `?org=${autoOrgId}` : ''}`)} data-testid="cc-automotive-manage-vehicles"
                                className="gap-2 justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Car className="w-4 h-4 text-slate-600" />{t('commandCenter.automotive.manageVehicles', 'Manage Used Vehicles')}
                            </Button>
                            <Button variant="outline" onClick={() => navigate('/vehicle-leads')} data-testid="cc-automotive-leads"
                                className="gap-2 justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Users className="w-4 h-4 text-slate-600" />{t('commandCenter.automotive.viewLeads', 'View Leads')}
                            </Button>
                        </div>
                    )}
                    {cfg.actionMode === 'ops' && (
                        <div className="flex flex-wrap gap-3">
                            <Button onClick={() => setDispatchOpen(true)} data-testid="cc-act-dispatch"
                                className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-amber-600 hover:bg-amber-500 text-white">
                                <Wrench className="w-4 h-4" />{t('commandCenter.act.dispatch.title')}
                            </Button>
                            <Button onClick={() => setCommOpen(true)} data-testid="cc-act-communicate"
                                variant="outline"
                                className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Send className="w-4 h-4 text-slate-600" />{t('commandCenter.act.communicate')}
                            </Button>
                            <Button onClick={() => setAnnounceOpen(true)} data-testid="cc-act-announce"
                                variant="outline"
                                className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700 text-slate-800 dark:text-slate-200">
                                <Megaphone className="w-4 h-4 text-slate-600" />{t('commandCenter.act.announce.title')}
                            </Button>
                            <Button variant="outline" onClick={() => setAssignOpen(true)} data-testid="cc-act-assign"
                                className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700">
                                <Users className="w-4 h-4 text-slate-600" />{t('commandCenter.act.assignStaff')}
                            </Button>
                            <Button variant="outline" data-testid="cc-act-cameras"
                                onClick={() => navigate(`/command-center/cameras${superAdmin && selectedOrg && selectedOrg !== 'all' ? `?org=${selectedOrg}` : ''}`)}
                                className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700">
                                <Video className="w-4 h-4 text-sky-400" />{t('commandCenter.cameras.title')}
                            </Button>
                            {vertical === 'hotel' && (
                                <Button variant="outline" data-testid="cc-act-room-housekeeping"
                                    onClick={() => { setRaLoc((available[0] && available[0].id) || ''); setRaRoom(''); setRaAction('ready'); setRaReason(''); setRoomActOpen(true); }}
                                    className="gap-2 flex-1 min-w-[150px] justify-center h-auto py-2 whitespace-normal text-center leading-tight bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700">
                                    <DoorOpen className="w-4 h-4 text-sky-400" />{t('commandCenter.act.roomHousekeeping')}
                                </Button>
                            )}
                        </div>
                    )}
                    </div>
                </div>
                )}
            </div>

            {scope.kind === 'none' || data?.empty ? (
                <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none" data-testid="cc-empty">
                    <CardContent className="p-4">
                        <EmptyState
                            icon={Building2}
                            title={t('commandCenter.friendly.allCaughtUp')}
                            description={friendly?.emptyKey ? t(friendly.emptyKey) : t('commandCenter.empty.noProperties')}
                            action={friendly ? (
                                <Button onClick={tour.start} data-testid="cc-empty-cta"
                                    className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500">
                                    <Compass className="w-4 h-4" />
                                    <span className="max-w-[200px] truncate">{t('commandCenter.friendly.helpButton')}</span>
                                </Button>
                            ) : null}
                        />
                    </CardContent>
                </Card>
            ) : (
                <>
                    {/* Alerts firing into an empty recipient list. Renders
                        nothing once a recipient exists (or with no inventory). */}
                    {cfg.actionMode === 'automotive' && (
                        <AlertsDeafBanner organizationId={autoOrgId}
                            onFix={() => navigate(`/used-vehicles?org=${autoOrgId}&settings=1`)} />
                    )}

                    {/* ── FRIENDLY TIP (per-vertical config only) ── */}
                    {friendly?.tip && (
                        <FriendlyTip id={friendly.tourId} titleKey={friendly.tip.titleKey} bodyKey={friendly.tip.bodyKey} />
                    )}

                    {/* ── THREE HEADLINE NUMBERS (per-vertical config only) ── */}
                    {friendly && (
                        <section data-testid="cc-friendly-numbers">
                            <FriendlyHeadline i18nKey="commandCenter.friendly.numbersHeadline" testid="cc-friendly-numbers-title" />
                            <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-5" data-testid="cc-headline-numbers">
                                {friendly.headlines.map((h) => (
                                    <div key={h.id} data-testid={`cc-headline-${h.id}`}
                                        className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none">
                                        <div className="flex items-start gap-1">
                                            <span className="text-xs uppercase tracking-wide text-slate-600 break-words">
                                                {t(h.labelKey)}
                                            </span>
                                            <HelpTooltip text={t(h.helpKey)} className="shrink-0" />
                                        </div>
                                        <div className="mt-3 text-5xl sm:text-6xl font-light leading-none text-slate-900 dark:text-white"
                                            style={{ fontFamily: 'Manrope, sans-serif' }}
                                            data-testid={`cc-headline-value-${h.id}`}>
                                            {fmtHeadline(ov[h.field])}
                                        </div>
                                        {h.unitKey && (
                                            <div className="mt-2 text-sm text-slate-600">{t(h.unitKey)}</div>
                                        )}
                                        {h.sub && (
                                            <div className="mt-2 text-sm text-slate-600 break-words" data-testid={`cc-headline-sub-${h.id}`}>
                                                {t(h.sub.labelKey)}: {ov[h.sub.field] ?? 0}
                                            </div>
                                        )}
                                        {h.subDeltaField && (
                                            <div className="mt-2" data-testid={`cc-headline-sub-${h.id}`}>
                                                <DeltaPill pct={wc[h.subDeltaField]} />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </section>
                    )}

                    {/* ── NEEDS A LOOK — sized to the information, never
                        alarming: one small calm pill that expands into a
                        compact list. Severity survives only as a small dot.
                        Same items, same "Take a look" action, every vertical. ── */}
                    {(() => {
                        const items = data.needs_attention || [];
                        const loud = items.length > 0;
                        // NOTHING OPEN = ONE SLIM 48px ROW, not a tall empty card.
                        if (!loud) return (
                            <div data-testid="cc-band-attention"
                                className="flex h-12 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none">
                                <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-500" />
                                <span className="truncate text-sm font-medium text-slate-800 dark:text-slate-200"
                                    data-testid="cc-all-caught-up">
                                    {t('commandCenter.friendly.allCaughtUp')}
                                </span>
                                <span className="hidden truncate text-sm text-slate-500 dark:text-slate-400 sm:inline"
                                    data-testid="cc-attention-empty">
                                    · {t('commandCenter.empty.noAttention')}
                                </span>
                            </div>
                        );
                        return (
                        <div data-testid="cc-band-attention"
                            className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none">
                            <button type="button"
                                data-testid="cc-attention-pill"
                                aria-expanded={attentionOpen}
                                onClick={() => setAttentionOpen((o) => !o)}
                                className="inline-flex max-w-full items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200 dark:hover:bg-slate-800">
                                <span className="h-2 w-2 shrink-0 rounded-full bg-slate-400 dark:bg-slate-500" />
                                <span className="truncate" data-testid="cc-attention-pill-label">
                                    {t('commandCenter.attention.toLookAt', { count: items.length })}
                                </span>
                                <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${attentionOpen ? 'rotate-90' : ''}`} />
                            </button>
                            {attentionOpen && (
                                <div data-testid="cc-attention-list"
                                    className="mt-2 divide-y divide-slate-100 dark:divide-slate-800">
                                    {items.map((n) => {
                                        const s = SEVERITY_STYLE[n.severity] || SEVERITY_STYLE.low;
                                        return (
                                            <div
                                                key={n.location_id}
                                                role="button"
                                                tabIndex={0}
                                                data-testid={`cc-attention-${n.location_id}`}
                                                onClick={() => takeALook(n)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') takeALook(n); }}
                                                className="flex cursor-pointer items-center gap-2.5 py-2 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/60"
                                            >
                                                <span className={`h-2 w-2 shrink-0 rounded-full ${s.dot}`} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">{n.name}</div>
                                                    <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                                                        {isOps ? (
                                                            <>
                                                                {t('commandCenter.ops.openCount', { count: n.open })}
                                                                {n.urgent > 0 && ' · ' + t('commandCenter.ops.urgentCount', { count: n.urgent })}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {n.open_feedback > 0 && t('commandCenter.attention.openFeedback', { count: n.open_feedback })}
                                                                {n.open_feedback > 0 && n.avg_rating != null && n.avg_rating < 4 && ' · '}
                                                                {n.avg_rating != null && n.avg_rating < 4 && t('commandCenter.attention.lowRating', { rating: n.avg_rating })}
                                                                {/* Only the medical adapter supplies topics, so every
                                                                    other vertical's row is unchanged. */}
                                                                {(n.top_categories || []).length > 0 && (
                                                                    <span data-testid={`cc-attention-topics-${n.location_id}`}>
                                                                        {' · '}
                                                                        {n.top_categories.map((c) => t(`reviewShield.improvementCategories.${c}`, c)).join(', ')}
                                                                    </span>
                                                                )}
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                                <Button size="sm" variant="ghost"
                                                    data-testid={`cc-attention-action-${n.location_id}`}
                                                    onClick={(e) => { e.stopPropagation(); takeALook(n); }}
                                                    className="h-7 shrink-0 gap-1 text-slate-600 dark:text-slate-300">
                                                    <span className="max-w-[140px] truncate text-xs">{t('commandCenter.friendly.look')}</span>
                                                    <ChevronRight className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        );
                    })()}

                    {/* ── OVERVIEW / KEY STATISTICS ── */}
                    <section data-testid="cc-band-overview">
                        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200 mb-4 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-emerald-400" />{t('commandCenter.band.overview')}
                        </h2>
                        <div className={`grid gap-4 ${OVERVIEW_GRID[overviewCount] || 'grid-cols-2 lg:grid-cols-4'}`}>
                            {cfg.overviewMode === 'automotive' ? (
                                <>
                                    {/* The three figures now headlined above are not repeated here. */}
                                    {!friendly && (
                                        <StatCard testid="cc-stat-vehicles" icon={Car} accent="text-amber-400"
                                            label={t('commandCenter.automotive.vehiclesTotal', 'Vehicles')} value={(ov.vehicles_total ?? 0).toLocaleString()}
                                            sub={t('commandCenter.automotive.vehiclesActive', 'Active') + ': ' + (ov.vehicles_active ?? 0)} />
                                    )}
                                    <StatCard testid="cc-stat-sold" icon={ShieldCheck} accent="text-emerald-400"
                                        label={t('commandCenter.automotive.vehiclesSold', 'Sold')} value={ov.vehicles_sold ?? 0}
                                        sub={t('commandCenter.automotive.vehiclesActive', 'Active') + ': ' + (ov.vehicles_active ?? 0)} />
                                    {!friendly && (
                                        <StatCard testid="cc-stat-testdrives-kpi" icon={AlertTriangle} accent="text-amber-400"
                                            label={t('commandCenter.automotive.testDrivesPending', 'Test drives pending')} value={ov.test_drives_pending ?? 0}
                                            sub={t('commandCenter.automotive.testDrivesBooked', 'Booked') + ': ' + (ov.test_drives_booked ?? 0)} />
                                    )}
                                    {!friendly && (
                                        <StatCard testid="cc-stat-inquiries" icon={Users} accent="text-blue-400"
                                            label={t('commandCenter.automotive.questions', 'Questions')} value={ov.open_inquiries ?? 0}
                                            sub={t('commandCenter.stat.locations') + ': ' + (ov.active_locations ?? scope.selected_count ?? 0)} />
                                    )}
                                    <StatCard testid="cc-stat-tradeins" icon={Repeat} accent="text-sky-400"
                                        label={t('commandCenter.automotive.tradeInsOpen', 'Trade-ins to appraise')} value={ov.trade_ins_open ?? 0}
                                        sub={t('commandCenter.automotive.tradeInsSub', 'Waiting on a number')} />
                                </>
                            ) : isOps ? (
                                <>
                                    {/* Verticals with headline numbers above do not repeat them here. */}
                                    {!friendly && (
                                        <StatCard testid="cc-stat-open" icon={AlertTriangle} accent="text-amber-400"
                                            label={t('commandCenter.ops.openRequests')} value={(ov.open_requests ?? 0).toLocaleString()}
                                            sub={<DeltaPill pct={wc.requests_delta_pct} />} />
                                    )}
                                    {!friendly && (
                                        <StatCard testid="cc-stat-urgent" icon={AlertTriangle} accent="text-red-400"
                                            label={t('commandCenter.ops.urgent')} value={ov.urgent_open ?? 0}
                                            sub={t('commandCenter.ops.resolved7d') + ': ' + (ov.resolved_7d ?? 0)} />
                                    )}
                                    {!friendly && (
                                        <StatCard testid="cc-stat-response" icon={Activity} accent="text-blue-400"
                                            label={t('commandCenter.ops.avgResponse')}
                                            value={ov.avg_response_min != null ? `${ov.avg_response_min}` : '—'}
                                            sub={t('commandCenter.ops.minutes')} />
                                    )}
                                    <StatCard testid="cc-stat-units" icon={Building2} accent="text-orange-400"
                                        label={t('commandCenter.ops.units')} value={ov.units_total ?? 0}
                                        sub={t('commandCenter.stat.locations') + ': ' + (ov.active_locations ?? 0)} />
                                </>
                            ) : (
                                <>
                            <StatCard testid="cc-stat-reviews" icon={Star} accent="text-emerald-400"
                                label={t('commandCenter.stat.reviews')} value={(ov.total_reviews ?? 0).toLocaleString()}
                                sub={<DeltaPill pct={wc.reviews_delta_pct} />} />
                            <StatCard testid="cc-stat-value" icon={DollarSign} accent="text-blue-400"
                                label={t('commandCenter.stat.value')} value={fmtMoney(ov.total_value)}
                                sub={<DeltaPill pct={wc.value_delta_pct} />} />
                            <StatCard testid="cc-stat-velocity" icon={Activity} accent="text-purple-400"
                                label={t('commandCenter.stat.velocity')} value={ov.avg_daily_velocity ?? 0}
                                sub={t('commandCenter.stat.perDay')} />
                            <StatCard testid="cc-stat-locations" icon={Building2} accent="text-orange-400"
                                label={t(wording.locationsStat || 'commandCenter.stat.locations')} value={ov.active_locations ?? 0}
                                sub={ov.avg_rating != null ? `★ ${ov.avg_rating}` : t('commandCenter.stat.noRating')} />
                                </>
                            )}
                        </div>
                    </section>

                    {/* ── LOYALTY PROGRAM (industry-neutral, every vertical) ──
                        Deliberately NOT in the per-vertical panel registry: one
                        block serves every vertical, so no Command Center code is
                        ever written per industry. Renders only when this
                        organization's program is on or being set up AND the
                        viewer may set it up — false everywhere today. */}
                    {(data?.loyalty?.enabled || data?.loyalty?.setup_started || vipFocus)
                        && data?.loyalty?.can_setup && ccScopedOrgId && (
                        <LoyaltyProgramPanel data={data} organizationId={ccScopedOrgId}
                                             startOpen={vipFocus} />
                    )}

                    {/* The navigation entry with no organization chosen: one line,
                        because there is nothing to set up for "All organizations". */}
                    {vipFocus && data?.loyalty?.can_setup && !ccScopedOrgId && (
                        <p className="rounded-2xl border border-slate-200 bg-white p-5 text-sm
                                      text-slate-600 shadow-sm dark:border-slate-700/50
                                      dark:bg-slate-900/40 dark:text-slate-300 dark:shadow-none"
                           data-testid="cc-loyalty-pick-clinic">
                            {t('loyalty.cc.pickClinic', 'Pick a clinic to manage VIP Rewards.')}
                        </p>
                    )}

                    {/* ── VERTICAL PANELS (shared extension point) ──
                        Config-driven slot beneath the Overview KPI row. Inert in
                        Slice 3 (empty registry for every vertical); Slice 4
                        registers Automotive funnel/inventory here. */}
                    <VerticalPanels
                        vertical={vertical}
                        ctx={{
                            organizationId: autoOrgId,
                            scopedOrgId: ccScopedOrgId,
                            locationIds: selectedLocIds,
                            focusDept,
                            scope,
                            data,
                            t,
                            navigate,
                        }}
                    />

                    {cfg.actionMode === 'automotive' && (
                        <AutomotiveSalesTempDialog
                            open={salesTempOpen} onOpenChange={setSalesTempOpen}
                            organizationId={autoOrgId} locationIds={selectedLocIds}
                            onDispatch={(r) => {
                                const veh = [r.year, r.make, r.model].filter(Boolean).join(' ');
                                setDispatchPreset(t('commandCenter.automotive.dispatchPreset',
                                    { vehicle: veh, stock: r.stock_number,
                                      defaultValue: `Customer is looking at the ${veh} (#${r.stock_number}) on the sales lot. Please head out and assist them now.` }));
                                setDispatchOpen(true);
                            }}
                        />
                    )}

                    <div className="grid grid-cols-1 gap-6">
                        {/* ── WHAT'S HAPPENING / ACTIVITY ── */}
                        <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none" data-testid="cc-band-activity">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <Activity className="w-4 h-4 text-emerald-400" />{t('commandCenter.band.whatsHappening')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-2">
                                {(data.activity || []).length === 0 ? (
                                    <p className="text-sm text-slate-600 py-6 text-center" data-testid="cc-activity-empty">{t('commandCenter.empty.noActivity')}</p>
                                ) : (
                                    (data.activity || []).map((a, i) => (
                                        <button
                                            key={a.id || i}
                                            data-testid={`cc-activity-${i}`}
                                            onClick={() => a.deep_link && navigate(a.deep_link)}
                                            className="w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-slate-800/40 transition-colors"
                                        >
                                            <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${SEVERITY_STYLE[a.severity]?.dot || 'bg-slate-500'}`} />
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm text-slate-800 dark:text-slate-200 truncate">{humanizeActivity(a)}</p>
                                                {a.actor && <p className="text-xs text-slate-600">{a.actor}</p>}
                                            </div>
                                            {a.deep_link && <ChevronRight className="w-4 h-4 text-slate-600 mt-1" />}
                                        </button>
                                    ))
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* ── TRENDS ── */}
                        <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none lg:col-span-2" data-testid="cc-band-trends">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <TrendingUp className="w-4 h-4 text-emerald-400" />{t('commandCenter.band.trends')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                {(data.trends || []).length === 0 ? (
                                    <p className="text-sm text-slate-600 py-10 text-center">{t('commandCenter.empty.noData')}</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={220} minHeight={220}>
                                        <LineChart data={data.trends} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                                            <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 11 }} tickFormatter={(d) => d.slice(5)} />
                                            <YAxis tick={{ fill: '#64748b', fontSize: 11 }} allowDecimals={false} />
                                            <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0' }} />
                                            <Line type="monotone" dataKey={isOps ? 'requests' : 'reviews'} stroke="#34d399" strokeWidth={2} dot={{ r: 3, fill: '#34d399' }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>

                        {/* ── WHAT CHANGED + drill-down ── */}
                        <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none" data-testid="cc-band-whatchanged">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2 text-slate-800 dark:text-slate-200">
                                    <TrendingUp className="w-4 h-4 text-blue-400" />{t('commandCenter.band.whatChanged')}
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {isOps ? (
                                    <>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-600">{t('commandCenter.ops.openRequests')}</span>
                                            <DeltaPill pct={wc.requests_delta_pct} />
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-slate-600">{t('commandCenter.ops.requestsBand')} (7d)</span>
                                            <span className="text-sm font-semibold text-white">{wc.requests_current ?? 0}</span>
                                        </div>
                                        <p className="text-xs text-slate-600">{t('commandCenter.ops.requestsDelta')}</p>
                                    </>
                                ) : (
                                    <>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-600">{t('commandCenter.stat.reviews')}</span>
                                    <DeltaPill pct={wc.reviews_delta_pct} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-sm text-slate-600">{t('commandCenter.stat.value')}</span>
                                    <DeltaPill pct={wc.value_delta_pct} />
                                </div>
                                <p className="text-xs text-slate-600">{t('commandCenter.whatChanged.period', { days: wc.period_days || 30 })}</p>
                                <div className="pt-2 border-t border-slate-700/50 space-y-1.5">
                                    <p className="text-xs uppercase tracking-wide text-slate-600 mb-1">{t('commandCenter.drilldown.title')}</p>
                                    {[
                                        { to: '/executive-summary', label: t('commandCenter.drilldown.execSummary') },
                                        { to: '/review-shield-results', label: t('commandCenter.drilldown.reviewShield') },
                                        // A clinic sees ROI only once a clinic metric profile exists —
                                        // never a blank card, a zero, or another vertical's numbers.
                                        ...(vertical === 'medical' && !data.roi_available
                                            ? [] : [{ to: '/roi', label: t('commandCenter.drilldown.roi') }]),
                                        { to: '/analytics', label: t('commandCenter.drilldown.analytics') },
                                        // Patron Points is a super-admin section; no other
                                        // role sees this line, and nothing else in the list
                                        // changes order.
                                        ...(superAdmin
                                            ? [{ to: '/patron-points', label: t('commandCenter.drilldown.patronPoints') }]
                                            : []),
                                    ].map((d) => (
                                        <button key={d.to} data-testid={`cc-drill-${d.to.replace(/\//g, '')}`}
                                            onClick={() => navigate(ccScopedOrgId
                                                ? `${d.to}?org=${ccScopedOrgId}` : d.to)}
                                            className="w-full flex items-center justify-between text-sm text-slate-600 hover:text-white py-1 group">
                                            <span>{d.label}</span>
                                            <ArrowUpRight className="w-3.5 h-3.5 text-slate-600 group-hover:text-emerald-400" />
                                        </button>
                                    ))}
                                </div>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* ── OPEN REQUESTS (Hotel/Condo SEE list) ── */}
                    {isOps && (
                        <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none" data-testid="cc-band-requests">
                            <CardHeader className="pb-3" data-testid="cc-band-requests-header">
                                <CardTitle className="text-base flex items-center justify-between gap-2 text-slate-800 dark:text-slate-200">
                                    <span className="flex items-center gap-2">
                                        <Layers className="w-4 h-4 text-amber-400" />
                                        {/* Two tabs: what needs attention NOW, and what already happened. */}
                                        <button type="button" data-testid="cc-tab-open"
                                            onClick={() => setReqTab('open')}
                                            className={`transition-colors ${reqTab === 'open' ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}>
                                            {t('commandCenter.ops.requestsBand')}
                                        </button>
                                        <span className="text-slate-300 dark:text-slate-700">|</span>
                                        <button type="button" data-testid="cc-tab-recent"
                                            onClick={() => setReqTab('recent')}
                                            className={`flex items-center gap-1.5 transition-colors ${reqTab === 'recent' ? 'text-slate-800 dark:text-slate-200' : 'text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300'}`}>
                                            {t('commandCenter.recent.tab')}
                                            {/* Always-on light: RED = activity since you last looked, GREEN = nothing new. */}
                                            <span data-testid="cc-recent-light" data-state={recentUnseen > 0 ? 'new' : 'clear'}
                                                title={recentUnseen > 0 ? t('commandCenter.recent.newSinceLastVisit') : t('commandCenter.recent.nothingNew')}
                                                className={`w-2 h-2 rounded-full ${recentUnseen > 0 ? 'bg-red-500 animate-pulse' : 'bg-emerald-500'}`} />
                                        </button>
                                        {deptFilter && reqTab === 'open' && (
                                            <Badge className="bg-emerald-500/15 text-emerald-300 border-0 capitalize" data-testid="cc-requests-dept-chip">
                                                {t('commandCenter.departments.showing')}: {t(`commandCenter.act.dispatch.cat.${deptFilter}`, deptFilter.replace(/_/g, ' '))}
                                            </Badge>
                                        )}
                                    </span>
                                    {deptFilter && reqTab === 'open' && (
                                        <button data-testid="cc-requests-back-all" onClick={() => setDeptFilter(null)}
                                            className="flex items-center gap-1 text-xs font-normal text-slate-600 hover:text-white">
                                            <ChevronRight className="w-3.5 h-3.5 rotate-180" />{t('commandCenter.departments.backToAll')}
                                        </button>
                                    )}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <RecentActivity
                                    active={reqTab === 'recent'}
                                    orgId={selectedOrg} locationIds={selectedLocIds} vertical={vertical}
                                    refreshKey={data?.overview?.open_requests}
                                    onMeta={(m) => { if (m && typeof m.unseen === 'number') setRecentUnseen(m.unseen); }}
                                    onOpen={(row) => setDetailReq(row)}
                                />
                                {reqTab === 'open' && (() => {
                                    const counts = data.request_counts || {};
                                    const all = (data.requests || []).filter((r) => deptMatchesRequest(r, deptFilter));
                                    const rows = reqView === 'archived' ? all : all.filter((r) => (r.status || 'open') === reqView);
                                    const page = rows.slice(0, reqLimit);
                                    const pageItems = page.map((r) => ({ kind: r.kind, id: r.id }));
                                    const allOnPage = page.length > 0 && page.every((r) => isSelected(r));
                                    const switchView = (v) => {
                                        setReqView(v); reqViewRef.current = v; setReqSel([]); setReqLimit(10);
                                        fetchOverview(selectedOrg, selectedLocIds);
                                    };
                                    return (
                                    <>
                                        <div className="flex flex-wrap items-center gap-2 pb-3" data-testid="cc-requests-pills">
                                            {['open', 'acknowledged', 'archived'].map((v) => (
                                                <button key={v} type="button" data-testid={`cc-requests-pill-${v}`} data-active={reqView === v}
                                                    onClick={() => switchView(v)}
                                                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${reqView === v
                                                        ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                                                        : 'border-slate-200 dark:border-slate-700 text-slate-600 hover:border-slate-400'}`}>
                                                    {t(`commandCenter.requests.pill.${v}`)}
                                                    {typeof counts[v] === 'number' ? ` · ${counts[v]}` : ''}
                                                </button>
                                            ))}
                                        </div>
                                        {reqSel.length > 0 && (
                                            <div className="flex flex-wrap items-center gap-2 mb-3 rounded-xl border border-emerald-500/40 bg-emerald-500/5 px-3 py-2"
                                                data-testid="cc-requests-bulk-bar">
                                                <span className="text-xs text-slate-700 dark:text-slate-200">
                                                    {t('commandCenter.requests.selected', { count: reqSel.length })}
                                                </span>
                                                <Button size="sm" data-testid="cc-requests-bulk-archive"
                                                    onClick={() => archiveRequests(reqSel, reqView !== 'archived')}
                                                    className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-500 text-white">
                                                    {reqView === 'archived'
                                                        ? <ArchiveRestore className="w-3.5 h-3.5 mr-1" />
                                                        : <Archive className="w-3.5 h-3.5 mr-1" />}
                                                    {t(reqView === 'archived' ? 'commandCenter.requests.restoreSelected' : 'commandCenter.requests.archiveSelected')}
                                                </Button>
                                                <Button size="sm" variant="ghost" data-testid="cc-requests-bulk-clear"
                                                    onClick={() => setReqSel([])} className="h-7 px-2 text-xs text-slate-600">
                                                    {t('commandCenter.requests.clearSelection')}
                                                </Button>
                                            </div>
                                        )}
                                        {rows.length === 0 ? (
                                            <p className="text-sm text-slate-600 py-6 text-center" data-testid="cc-requests-empty">
                                                {t(reqView === 'archived' ? 'commandCenter.requests.noArchived' : 'commandCenter.ops.noRequests')}
                                            </p>
                                        ) : (
                                        <>
                                        <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-slate-800">
                                            <Checkbox checked={allOnPage} data-testid="cc-requests-select-all"
                                                onCheckedChange={(v) => setReqSel(v ? pageItems : [])} />
                                            <span className="text-[11px] uppercase tracking-wide text-slate-600">
                                                {t('commandCenter.requests.selectAllOnPage')}
                                            </span>
                                        </div>
                                        <div className="divide-y divide-slate-200 dark:divide-slate-800">
                                        {page.map((r) => (
                                            <div key={r.id} data-testid={`cc-request-${r.id}`} className="flex items-center gap-3 py-2.5">
                                                <Checkbox checked={isSelected(r)} data-testid={`cc-req-select-${r.id}`}
                                                    onCheckedChange={() => toggleSelected(r)} />
                                                <span data-testid={`cc-req-dot-${r.id}`} data-status={r.status}
                                                    title={r.urgent ? t('commandCenter.ops.urgent') : ''}
                                                    className={`w-2.5 h-2.5 rounded-full shrink-0 ${REQ_DOT(r.status)} ${r.status === 'open' && r.urgent ? 'animate-pulse' : ''}`} />
                                                <div className="min-w-0 flex-1 cursor-pointer" role="button" tabIndex={0}
                                                    data-testid={`cc-req-open-${r.id}`} onClick={() => setDetailReq(r)}>
                                                    <div className="flex items-center gap-2">
                                                        <Badge variant="outline" className="border-slate-600 text-slate-400 text-[10px] py-0 shrink-0 capitalize" data-testid={`cc-req-dept-${r.id}`}>
                                                            {t(`commandCenter.dept.${r.kind}`, (r.kind || '').replace(/_/g, ' '))}
                                                        </Badge>
                                                        <span className="text-sm text-slate-800 dark:text-slate-200 capitalize truncate hover:text-white transition-colors">{t(`commandCenter.act.dispatch.cat.${r.category}`, (r.category || r.kind).replace(/_/g, ' '))}</span>
                                                        {r.room_number && <Badge variant="outline" className="border-slate-700 text-slate-600 text-[10px] py-0">{t('commandCenter.ops.room')} {r.room_number}</Badge>}
                                                        {r.urgent && <Badge className="bg-red-500/20 text-red-300 text-[10px] py-0 border-0">{t('commandCenter.ops.urgent')}</Badge>}
                                                        {r.pending_routing && (
                                                            <Badge className="bg-violet-500/20 text-violet-300 text-[10px] py-0 border-0"
                                                                data-testid={`cc-req-awaiting-routing-${r.id}`}
                                                                title={r.escalation_reason || ''}>
                                                                {t('commandCenter.act.awaitingRouting')}
                                                                {r.escalated_from_department ? ` · ${t(`commandCenter.act.dept.${r.escalated_from_department}`, r.escalated_from_department)}` : ''}
                                                            </Badge>
                                                        )}
                                                        {r.origin === 'manager_dispatch' && <Badge className="bg-amber-500/20 text-amber-300 text-[10px] py-0 border-0" data-testid={`cc-req-dispatch-${r.id}`}>{t('commandCenter.ops.dispatch')}</Badge>}
                                                        {liveBuyerIds.has(r.id) && <LiveNowBadge testid={`cc-req-live-${r.id}`} />}
                                                    </div>
                                                    {r.message && <p className="text-xs text-slate-600 truncate">{r.message}</p>}
                                                    {(() => {
                                                        // Who / which item / how long ago — existing fields only.
                                                        // A field the vertical does not store simply does not render.
                                                        const who = r.who || r.customer_name;
                                                        // Skip the item when the row already shows it as a badge.
                                                        const item = r.room_number ? null : (r.item || r.stock_number);
                                                        if (!who && !item && !r.created_at) return null;
                                                        return (
                                                            <p className="mt-0.5 text-[11px] text-slate-600 flex flex-wrap items-center gap-x-2"
                                                                data-testid={`cc-req-meta-${r.id}`}>
                                                                {who && <span className="truncate max-w-[160px]">{who}</span>}
                                                                {item && (
                                                                    <span>
                                                                        {friendly?.itemLabelKey ? `${t(friendly.itemLabelKey)} ` : ''}{item}
                                                                    </span>
                                                                )}
                                                                {r.created_at && (
                                                                    <span className="inline-flex items-center gap-1">
                                                                        <Clock className="w-3 h-3" />
                                                                        {formatRelative(r.created_at, { lang: i18n.language })}
                                                                    </span>
                                                                )}
                                                            </p>
                                                        );
                                                    })()}
                                                    {r.assigned_staff_name && <p className="text-[11px] text-sky-400/90 truncate" data-testid={`cc-req-assigned-${r.id}`}>{t('commandCenter.ops.assigned')}: {r.assigned_staff_name}</p>}
                                                </div>
                                                <span className="text-xs text-slate-600 shrink-0">{r.location_name}</span>
                                                <button data-testid={`cc-req-status-${r.id}`} onClick={() => setDetailReq(r)}
                                                    className="shrink-0 text-[10px] py-0.5 px-2 rounded-full border border-slate-700 text-slate-600 capitalize hover:border-slate-500 hover:text-white transition-colors">
                                                    {t(`commandCenter.status.${r.status}`, (r.status || '').replace(/_/g, ' '))}
                                                </button>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    {vertical === 'hotel' && r.kind !== 'valet' && r.room_number && r.location_id && (
                                                        <Popover>
                                                            <PopoverTrigger asChild>
                                                                <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                    data-testid={`cc-room-actions-${r.id}`}
                                                                    className="h-7 px-2 text-xs border-slate-600">
                                                                    <DoorOpen className="w-3.5 h-3.5 sm:mr-1" />
                                                                    <span className="hidden sm:inline">{t('commandCenter.ops.room')}</span>
                                                                </Button>
                                                            </PopoverTrigger>
                                                            <PopoverContent align="end" className="w-64 bg-slate-900 border-slate-700 p-3 space-y-2">
                                                                <p className="text-[11px] uppercase tracking-wide text-slate-600 pb-0.5">
                                                                    {t('commandCenter.act.roomStatus')} · {t('commandCenter.ops.room')} {r.room_number}
                                                                </p>
                                                                <Button size="sm" disabled={actingId === r.id}
                                                                    data-testid={`cc-housekeeping-ready-${r.id}`}
                                                                    onClick={() => actHousekeepingReady(r)}
                                                                    className="w-full h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white justify-start">
                                                                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-300 mr-2" />{t('commandCenter.act.markReady')}
                                                                </Button>
                                                                {flagRoomId === r.id ? (
                                                                    <div className="space-y-1.5">
                                                                        <div className="flex flex-wrap gap-1">
                                                                            {ROOM_REASON_PRESETS.map((pk) => (
                                                                                <button key={pk} type="button"
                                                                                    data-testid={`cc-room-preset-${pk}-${r.id}`}
                                                                                    onClick={() => setRoomReason(t(`commandCenter.act.preset.${pk}`))}
                                                                                    className="px-2 py-1 rounded-full text-[10px] border border-slate-700 text-slate-600 hover:bg-slate-800">
                                                                                    {t(`commandCenter.act.preset.${pk}`)}
                                                                                </button>
                                                                            ))}
                                                                        </div>
                                                                        <Input autoFocus value={roomReason}
                                                                            onChange={(e) => setRoomReason(e.target.value)}
                                                                            placeholder={t('commandCenter.act.reasonPlaceholder')}
                                                                            className="h-8 text-xs bg-slate-800 border-slate-700 text-slate-100"
                                                                            data-testid={`cc-room-reason-${r.id}`} />
                                                                        <div className="flex gap-1.5">
                                                                            <Button size="sm" disabled={!roomReason.trim() || actingId === r.id}
                                                                                data-testid={`cc-room-red-${r.id}`}
                                                                                onClick={() => actRoomStatus(r, 'red', roomReason.trim())}
                                                                                className="flex-1 h-8 text-xs bg-red-600 hover:bg-red-500 text-white">
                                                                                {t('commandCenter.act.flagConfirm')}
                                                                            </Button>
                                                                            <Button size="sm" variant="ghost"
                                                                                onClick={() => { setFlagRoomId(null); setRoomReason(''); }}
                                                                                className="h-8 text-xs text-slate-600">
                                                                                {t('commandCenter.act.cancel')}
                                                                            </Button>
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                        data-testid={`cc-room-flag-${r.id}`}
                                                                        onClick={() => { setFlagRoomId(r.id); setRoomReason(''); }}
                                                                        className="w-full h-8 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10 justify-start">
                                                                        <span className="w-2.5 h-2.5 rounded-full bg-red-500 mr-2" />{t('commandCenter.act.flagAttention')}
                                                                    </Button>
                                                                )}
                                                            </PopoverContent>
                                                        </Popover>
                                                    )}
                                                    {r.kind === 'valet' && (
                                                        <>
                                                            {valetNext(r.status) && (
                                                                <Button size="sm" disabled={actingId === r.id}
                                                                    data-testid={`cc-valet-advance-${r.id}`}
                                                                    onClick={() => actValet(r, valetNext(r.status).to)}
                                                                    className="h-7 px-2.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white">
                                                                    <Car className="w-3.5 h-3.5 mr-1" />{t(`commandCenter.act.${valetNext(r.status).key}`)}
                                                                </Button>
                                                            )}
                                                            <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                data-testid={`cc-valet-cancel-${r.id}`}
                                                                onClick={() => actValet(r, 'cancelled')}
                                                                className="h-7 px-2 text-xs border-slate-600 text-slate-600">
                                                                {t('commandCenter.act.valetCancel')}
                                                            </Button>
                                                        </>
                                                    )}
                                                    {r.kind !== 'valet' && (
                                                        <>
                                                            {r.status === 'open' && (
                                                                <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                    data-testid={`cc-req-ack-${r.id}`}
                                                                    onClick={() => actRequest(r.kind, r.id, 'acknowledge')}
                                                                    className="h-7 px-2 text-xs border-slate-600">
                                                                    {t('commandCenter.act.acknowledge')}
                                                                </Button>
                                                            )}
                                                            <Button size="sm" disabled={actingId === r.id}
                                                                data-testid={`cc-req-resolve-${r.id}`}
                                                                onClick={() => actRequest(r.kind, r.id, 'resolve')}
                                                                className="h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-500 text-white">
                                                                {t('commandCenter.act.resolve')}
                                                            </Button>
                                                            {!AUTOMOTIVE_KINDS.has(r.kind) && (
                                                                <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                    data-testid={`cc-req-reassign-${r.id}`}
                                                                    onClick={() => setReassign({ kind: r.kind, id: r.id, locationId: r.location_id, current: r.assigned_staff_name, department: r.department })}
                                                                    className="h-7 px-2 text-xs border-sky-600/50 text-sky-300 hover:bg-sky-500/10">
                                                                    {t('commandCenter.act.reassign')}
                                                                </Button>
                                                            )}
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button size="sm" variant="outline" disabled={actingId === r.id}
                                                                        data-testid={`cc-req-more-${r.id}`}
                                                                        className="h-7 w-7 p-0 border-slate-600">
                                                                        <MoreHorizontal className="w-4 h-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end" className="w-48">
                                                                    <DropdownMenuItem data-testid={`cc-req-cancel-${r.id}`}
                                                                        onClick={() => cancelRequest(r.kind, r.id)}>
                                                                        {t('commandCenter.act.cancelRequest')}
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem data-testid={`cc-req-archive-${r.id}`}
                                                                        onClick={() => archiveRequests([{ kind: r.kind, id: r.id }], !r.archived_at)}>
                                                                        {r.archived_at
                                                                            ? <><ArchiveRestore className="w-3.5 h-3.5 mr-2" />{t('commandCenter.requests.restore')}</>
                                                                            : <><Archive className="w-3.5 h-3.5 mr-2" />{t('commandCenter.requests.archive')}</>}
                                                                    </DropdownMenuItem>
                                                                    {canDeleteRequests && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem data-testid={`cc-req-delete-${r.id}`}
                                                                                onClick={() => setDeleteReq(r)}
                                                                                className="text-red-600 dark:text-red-400 focus:text-red-600">
                                                                                <Trash2 className="w-3.5 h-3.5 mr-2" />{t('commandCenter.requests.delete')}
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {rows.length > page.length && (
                                        <div className="pt-3 text-center">
                                            <Button size="sm" variant="outline" data-testid="cc-requests-show-more"
                                                onClick={() => setReqLimit((n) => n + 10)}
                                                className="h-7 px-3 text-xs bg-white border-slate-200 dark:bg-slate-900/60 dark:border-slate-700">
                                                {t('commandCenter.requests.showMore', { count: rows.length - page.length })}
                                            </Button>
                                        </div>
                                    )}
                                    </>
                                    )}
                                    </>
                                    ); })()}
                            </CardContent>
                        </Card>
                    )}

                    {/* ── Staff adoption footer stat ── */}
                    {ov.staff_adoption && ov.staff_adoption.total_employees > 0 && (
                        <Card className="border-slate-200 bg-white shadow-sm dark:border-slate-700/50 dark:bg-slate-900/40 dark:shadow-none" data-testid="cc-band-staff">
                            <CardContent className="p-5 flex items-center gap-4">
                                <Users className="w-5 h-5 text-purple-400" />
                                <div className="flex-1">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-slate-600">{t('commandCenter.stat.staffAdoption')}</span>
                                        <span className="text-sm font-semibold text-white">{ov.staff_adoption.rate}%</span>
                                    </div>
                                    <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
                                        <div className="h-full bg-purple-500" style={{ width: `${Math.min(100, ov.staff_adoption.rate)}%` }} />
                                    </div>
                                    <p className="mt-1 text-xs text-slate-600">
                                        {t('commandCenter.stat.staffActive', { active: ov.staff_adoption.active_employees, total: ov.staff_adoption.total_employees })}
                                    </p>
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </>
            )}

            {/* ── ACT drawers (Phase 3A) ── */}
            {isOps && (
                <>
                    <DispatchTaskDrawer
                        open={dispatchOpen} onOpenChange={setDispatchOpen}
                        vertical={vertical}
                        presetMessage={dispatchPreset}
                        locations={selectedLocIds.length > 0 ? available.filter((l) => selectedLocIds.includes(l.id)) : available}
                        defaultLocationId={selectedLocIds[0] || available[0]?.id}
                        onSent={() => fetchOverview(selectedOrg, selectedLocIds)}
                    />
                    <ReassignDialog
                        state={reassign} onClose={() => setReassign(null)}
                        onDone={() => { setReassign(null); fetchOverview(selectedOrg, selectedLocIds); }}
                    />
                    {/* Lives OUTSIDE the request dialog: it is a fixed overlay, and
                        the split-screen manager pane opens it with no request
                        dialog on screen at all. */}
                    <BuyerActivityPanel conversationId={buyerActivityFor} open={!!buyerActivityFor}
                        autoGoLive={ccAutoLive.current}
                        onClose={() => setBuyerActivityFor(null)} />
                    <Dialog open={!!detailReq} onOpenChange={(o) => !o && setDetailReq(null)}>
                        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-[88vh] overflow-y-auto" data-testid="cc-req-detail-dialog">
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <span className={`w-2.5 h-2.5 rounded-full ${REQ_DOT(detailReq?.status)}`} />
                                    {detailReq && t(`commandCenter.act.dispatch.cat.${detailReq.category}`, (detailReq.category || detailReq.kind || '').replace(/_/g, ' '))}
                                    {detailReq?.id && liveBuyerIds.has(detailReq.id) && <LiveNowBadge testid="cc-detail-live" />}
                                    {detailReq?.id && (
                                        <button type="button" onClick={() => setBuyerActivityFor(detailReq.id)}
                                            data-testid="cc-buyer-activity-btn"
                                            className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-100 bg-emerald-600/80 hover:bg-emerald-500 rounded-full px-3 py-1.5 transition-colors">
                                            <Activity className="w-3.5 h-3.5" />{t('buyerActivity.button', 'Buyer Activity')}
                                        </button>
                                    )}
                                </DialogTitle>
                                <DialogDescription className="sr-only">{t('commandCenter.ops.requestDetailDesc', 'Request details, status timeline and conversation')}</DialogDescription>
                            </DialogHeader>
                            {detailReq && (
                                <div className="space-y-2 text-sm">
                                    {detailReq.card_id && (
                                        <AutoInquiryVehicle cardId={detailReq.card_id} vehicleId={detailReq.vehicle_id} organizationId={autoOrgId || selectedOrg} />
                                    )}
                                    {isOfferRequest(detailReq) && (
                                        <div className="pt-1" data-testid="cc-detail-offer">
                                            <OfferDepositCard req={detailReq} tone="dark"
                                                onDecided={() => { setDetailReq(null); fetchOverview(selectedOrg, selectedLocIds); }} />
                                        </div>
                                    )}
                                    {detailReq.kind === 'trade_in' && (
                                        <CCTradeInPanel req={detailReq}
                                            onSaved={() => { setDetailReq(null); fetchOverview(selectedOrg, selectedLocIds); }} />
                                    )}
                                    {detailReq.message && <p className="text-slate-800 dark:text-slate-200">{detailReq.message}</p>}
                                    <div className="grid grid-cols-3 gap-2 text-xs pt-1">
                                        <span className="text-slate-600">{t('commandCenter.ops.status')}</span>
                                        <span className="col-span-2 text-slate-800 dark:text-slate-200 capitalize">{t(`commandCenter.status.${detailReq.status}`, (detailReq.status || '').replace(/_/g, ' '))}{detailReq.urgent ? ` · ${t('commandCenter.ops.urgent')}` : ''}</span>
                                        <span className="text-slate-600">{t('commandCenter.ops.location', 'Location')}</span>
                                        <span className="col-span-2 text-slate-800 dark:text-slate-200">{detailReq.location_name}{detailReq.room_number ? ` · ${t('commandCenter.ops.room')} ${detailReq.room_number}` : ''}</span>
                                        {detailReq.assigned_staff_name && <>
                                            <span className="text-slate-600">{t('commandCenter.ops.assigned')}</span>
                                            <span className="col-span-2 text-sky-300">{detailReq.assigned_staff_name}</span>
                                        </>}
                                        {detailReq.created_at && <>
                                            <span className="text-slate-600">{t('commandCenter.ops.tsCreated')}</span>
                                            <span className="col-span-2 text-slate-800 dark:text-slate-200" data-testid="cc-detail-ts-created">{new Date(detailReq.created_at).toLocaleString()}</span>
                                        </>}
                                        {detailReq.acknowledged_at && <>
                                            <span className="text-slate-600">{t('commandCenter.ops.tsAcknowledged')}</span>
                                            <span className="col-span-2 text-amber-300" data-testid="cc-detail-ts-ack">{new Date(detailReq.acknowledged_at).toLocaleString()}</span>
                                        </>}
                                        {detailReq.resolved_at && <>
                                            <span className="text-slate-600">{t('commandCenter.ops.tsResolved')}</span>
                                            <span className="col-span-2 text-emerald-300" data-testid="cc-detail-ts-resolved">{new Date(detailReq.resolved_at).toLocaleString()}</span>
                                        </>}
                                    </div>
                                    {/* FULL TIMELINE — created / acknowledged / responded / resolved,
                                        each with who did it. Shared across every vertical. */}
                                    <div className="pt-3 border-t border-slate-800">
                                        <ActionTimeline
                                            testid="cc-detail-timeline"
                                            fetcher={() => actionHistoryAPI.timeline(detailReq.kind, detailReq.id, selectedOrg && selectedOrg !== 'all' ? selectedOrg : undefined)}
                                            labels={{
                                                title: t('actionHistory.timelineTitle', 'Full history'),
                                                loading: t('actionHistory.loading', 'Loading history…'),
                                                empty: t('actionHistory.timelineEmpty', 'No history recorded for this request yet.'),
                                                by: t('actionHistory.by', 'by'),
                                                noActor: t('actionHistory.noActor', 'name not recorded for this step'),
                                                step: {
                                                    created: t('actionHistory.step.created', 'Created'),
                                                    acknowledged: t('actionHistory.step.acknowledged', 'Acknowledged'),
                                                    responded: t('actionHistory.step.responded', 'Responded'),
                                                    resolved: t('actionHistory.step.resolved', 'Resolved'),
                                                    cancelled: t('actionHistory.step.cancelled', 'Cancelled'),
                                                    reassign: t('actionHistory.step.reassign', 'Routed'),
                                                },
                                                dept: {
                                                    housekeeping: t('commandCenter.act.dept.housekeeping', 'Housekeeping'),
                                                    security: t('commandCenter.act.dept.security', 'Security'),
                                                    concierge: t('commandCenter.act.dept.concierge', 'Guest Requests'),
                                                    maintenance: t('commandCenter.act.dept.maintenance', 'Maintenance'),
                                                    parking: t('commandCenter.act.dept.parking', 'Parking'),
                                                    waste_recycling: t('commandCenter.act.dept.waste_recycling', 'Waste & Recycling'),
                                                    amenities: t('commandCenter.act.dept.amenities', 'Amenities'),
                                                    building: t('commandCenter.act.dept.building', 'Building'),
                                                    valet: t('commandCenter.act.dept.valet', 'Valet'),
                                                    management: t('commandCenter.act.dept.management', 'Management'),
                                                },
                                            }}
                                        />
                                    </div>
                                    {/* PRIMARY: respond to the buyer — leads the modal */}
                                    <div className="pt-3 border-t border-slate-800">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-semibold text-sky-600 dark:text-sky-300">{t('commandCenter.comm.replyToBuyer', 'Reply to the buyer')}</p>
                                            {AUTOMOTIVE_KINDS.has(detailReq.kind) && detailReq.card_id && (
                                                <button onClick={() => setSalesChatOpen((v) => !v)} data-testid="cc-detail-message-sales"
                                                    className="inline-flex items-center gap-1.5 rounded-lg bg-orange-600 hover:bg-orange-500 text-white text-xs font-semibold px-3 py-1.5 shadow-sm transition-colors">
                                                    <MessagesSquare className="w-4 h-4" />{t('commandCenter.automotive.messageSales', 'Message sales')}
                                                </button>
                                            )}
                                        </div>
                                        <RequestThread myRole="manager" autoFocus
                                            list={() => api.get(`/command-center/requests/${detailReq.kind}/${detailReq.id}/messages`).then((r) => r.data)}
                                            send={(body) => api.post(`/command-center/requests/${detailReq.kind}/${detailReq.id}/messages`, { body })}
                                        />
                                        {salesChatOpen && AUTOMOTIVE_KINDS.has(detailReq.kind) && (
                                            <div className="mt-3">
                                                <ManagerSalesChat organizationId={autoOrgId || selectedOrg} onClose={() => setSalesChatOpen(false)} />
                                            </div>
                                        )}
                                    </div>
                                    {/* SECONDARY: lifecycle actions — quieter row, below the conversation.
                                        Only for records that are still live: a record reached from
                                        Recent Activity is already closed and must not be mutated. */}
                                    {detailReq.kind !== 'valet' && ['open', 'acknowledged'].includes(detailReq.status) && (
                                        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-800" data-testid="cc-detail-manage-row">
                                            <span className="text-[11px] uppercase tracking-wide text-slate-600 mr-1">{t('commandCenter.act.manage', 'Manage')}</span>
                                            {detailReq.status === 'open' && (
                                                <Button size="sm" variant="ghost" disabled={actingId === detailReq.id} data-testid="cc-detail-ack"
                                                    onClick={async () => { await actRequest(detailReq.kind, detailReq.id, 'acknowledge'); setDetailReq(null); }}
                                                    className="h-7 px-2.5 text-xs text-slate-600 hover:text-slate-800 dark:hover:text-slate-100">{t('commandCenter.act.acknowledge')}</Button>
                                            )}
                                            <Button size="sm" variant="ghost" disabled={actingId === detailReq.id} data-testid="cc-detail-resolve"
                                                onClick={async () => { await actRequest(detailReq.kind, detailReq.id, 'resolve'); setDetailReq(null); }}
                                                className="h-7 px-2.5 text-xs text-slate-600 hover:text-emerald-600 dark:hover:text-emerald-300">{t('commandCenter.act.resolve')}</Button>
                                            {!AUTOMOTIVE_KINDS.has(detailReq.kind) && (
                                                <Button size="sm" variant="ghost" disabled={actingId === detailReq.id} data-testid="cc-detail-reassign"
                                                    onClick={() => { setReassign({ kind: detailReq.kind, id: detailReq.id, locationId: detailReq.location_id, current: detailReq.assigned_staff_name }); setDetailReq(null); }}
                                                    className="h-7 px-2.5 text-xs text-slate-600 hover:text-sky-600 dark:hover:text-sky-300">{t('commandCenter.act.reassign')}</Button>
                                            )}
                                            <Button size="sm" variant="ghost" disabled={actingId === detailReq.id} data-testid="cc-detail-cancel"
                                                onClick={async () => { const r = detailReq; setDetailReq(null); await cancelRequest(r.kind, r.id); }}
                                                className="h-7 px-2.5 text-xs text-slate-600 hover:text-red-500 dark:hover:text-red-300 ml-auto">{t('commandCenter.act.cancelRequest')}</Button>
                                        </div>
                                    )}
                                </div>
                            )}
                            <DialogFooter>
                                <Button variant="ghost" onClick={() => setDetailReq(null)} data-testid="cc-detail-close" className="text-slate-600">{t('commandCenter.ops.close')}</Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                    <CommunicationDrawer
                        open={commOpen} onOpenChange={setCommOpen}
                        vertical={vertical}
                        locations={selectedLocIds.length > 0 ? available.filter((l) => selectedLocIds.includes(l.id)) : available}
                        defaultLocationId={selectedLocIds[0] || available[0]?.id}
                        onSent={() => fetchOverview(selectedOrg, selectedLocIds)}
                    />
                    <AssignStaffDrawer
                        open={assignOpen} onOpenChange={setAssignOpen}
                        locations={selectedLocIds.length > 0 ? available.filter((l) => selectedLocIds.includes(l.id)) : available}
                        defaultLocationId={selectedLocIds[0] || available[0]?.id}
                    />
                    <GroupMessageDrawer
                        open={announceOpen} onOpenChange={setAnnounceOpen}
                        locations={selectedLocIds.length > 0 ? available.filter((l) => selectedLocIds.includes(l.id)) : available}
                        defaultLocationId={selectedLocIds[0] || available[0]?.id}
                    />
                    {vertical === 'hotel' && (
                        <Dialog open={roomActOpen} onOpenChange={setRoomActOpen}>
                            <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" data-testid="cc-room-act-dialog">
                                <DialogHeader>
                                    <DialogTitle>{t('commandCenter.act.roomHousekeeping')}</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <label className="text-xs uppercase tracking-wide text-slate-600">{t('commandCenter.act.selectLocation')}</label>
                                        <Select value={raLoc} onValueChange={setRaLoc}>
                                            <SelectTrigger className="bg-slate-800 border-slate-700" data-testid="cc-ra-location"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {available.map((l) => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs uppercase tracking-wide text-slate-600">{t('commandCenter.act.roomNumber')}</label>
                                        <Input data-testid="cc-ra-room" value={raRoom} onChange={(e) => setRaRoom(e.target.value)}
                                            placeholder="101" className="bg-slate-800 border-slate-700" />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        {[['ready', 'markReady', 'bg-emerald-500'], ['flag', 'flagAttention', 'bg-red-500']].map(([a, key, dot]) => (
                                            <button key={a} data-testid={`cc-ra-action-${a}`} onClick={() => setRaAction(a)}
                                                className={`col-span-3 sm:col-span-1 flex items-center gap-2 py-2 px-3 rounded-lg border text-sm ${raAction === a ? 'border-sky-500 bg-sky-500/10' : 'border-slate-700'}`}>
                                                <span className={`w-2.5 h-2.5 rounded-full ${dot}`} />{t(`commandCenter.act.${key}`)}
                                            </button>
                                        ))}
                                    </div>
                                    {raAction === 'flag' && (
                                        <Input data-testid="cc-ra-reason" value={raReason} onChange={(e) => setRaReason(e.target.value)}
                                            placeholder={t('commandCenter.act.reasonPlaceholder')} className="bg-slate-800 border-slate-700" />
                                    )}
                                </div>
                                <DialogFooter>
                                    <Button variant="ghost" onClick={() => setRoomActOpen(false)}>{t('commandCenter.act.cancel')}</Button>
                                    <Button data-testid="cc-ra-confirm"
                                        disabled={!raLoc || !raRoom.trim() || (raAction === 'flag' && !raReason.trim()) || actingId === 'mgr'}
                                        onClick={async () => {
                                            const r = { id: 'mgr', location_id: raLoc, room_number: raRoom.trim() };
                                            if (raAction === 'ready') await actHousekeepingReady(r);
                                            else await actRoomStatus(r, 'red', raReason.trim());
                                            setRoomActOpen(false);
                                        }}
                                        className="bg-sky-600 hover:bg-sky-500 text-white">
                                        {t('commandCenter.act.confirm')}
                                    </Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </>
            )}

            {/* ── PERMANENT DELETE (super_admin / client_admin, enforced server-side) ── */}
            <AlertDialog open={!!deleteReq} onOpenChange={(o) => { if (!o) setDeleteReq(null); }}>
                <AlertDialogContent data-testid="cc-req-delete-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('commandCenter.requests.deleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>{t('commandCenter.requests.deleteBody')}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="cc-req-delete-cancel">{t('commandCenter.act.cancel')}</AlertDialogCancel>
                        <AlertDialogAction data-testid="cc-req-delete-confirm"
                            onClick={() => deleteReq && deleteRequestNow(deleteReq)}
                            className="bg-red-600 hover:bg-red-500 text-white">
                            {t('commandCenter.requests.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* ── SHARED GUIDED TOUR (steps come from the vertical's config) ── */}
            {friendly && <GuidedTour steps={friendly.tourSteps} open={tour.open} onClose={tour.close}
                onDismissForever={disableTourForAccount} />}
        </div>
    );
}
